import React from 'react';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Box, 
  Typography,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import languageData from '../../config/languages.json';

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  minWidth: 200,
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
    },
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
  },
}));

const FlagIcon = styled(Box)(({ theme }) => ({
  fontSize: '1.5rem',
  marginRight: theme.spacing(1),
}));

const SelectedLanguage = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

/**
 * Enhanced language selector with flags
 * @param {Object} props Component props
 * @param {string} props.value Current language value
 * @param {Function} props.onChange Change handler
 * @param {Array} props.languages Array of language objects to display
 * @returns {JSX.Element} LanguageSelector component
 */
const LanguageSelector = ({ 
  value, 
  onChange, 
  languages = languageData.languages,
  ...props
}) => {
  // Create a map of language codes to language objects for easy lookup
  const languageMap = React.useMemo(() => {
    return languages.reduce((acc, lang) => {
      acc[lang.code] = lang;
      return acc;
    }, {});
  }, [languages]);

  return (
    <StyledFormControl {...props}>
      <InputLabel id="language-select-label">Language</InputLabel>
      <Select
        labelId="language-select-label"
        id="language-select"
        value={value}
        label="Language"
        onChange={onChange}
        renderValue={(selected) => {
          const lang = languageMap[selected] || { code: selected, name: selected, flag: '🌐' };
          return (
            <SelectedLanguage>
              <FlagIcon>{lang.flag}</FlagIcon>
              <Typography variant="body1">{lang.name}</Typography>
            </SelectedLanguage>
          );
        }}
      >
        {languages.map((lang, index) => (
          <MenuItem key={`${lang.code}-${index}`} value={lang.code}>
            <ListItemIcon sx={{ minWidth: 'auto', mr: 1 }}>
              {lang.flag}
            </ListItemIcon>
            <ListItemText primary={lang.name} />
          </MenuItem>
        ))}
      </Select>
    </StyledFormControl>
  );
};

export default LanguageSelector;
